import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { fetchFeatureUserStoriesApi, deleteUserStoryApi } from "@/api/api";

export interface UserStory {
    id: string;
    story_number: string;
    external_id: string | null;
    external_story_number: string | null;  // Jira ticket key (e.g., 'SCRUM-31')
    ticket_summary: string | null;
    title: string;
    user_story: string;
    description: string | null;
    acceptance_criteria: any;
    project_id: number;
    epic_id: string | null;
    feature_id: number | null;
    sprint_id: string | null;
    story_type: string | null;
    priority: string | null;
    story_points: number | null;
    business_value: number | null;
    risk_level: string | null;
    status: string;
    workflow_state: any;
    created_by: number;
    assigned_to: number | null;
    product_owner_id: number | null;
    reviewed_by: number | null;
    tested_by: number | null;
    created_at: string | null;
    updated_at: string | null;
    started_at: string | null;
    completed_at: string | null;
    accepted_at: string | null;
    due_date: string | null;
    blocked_by: string[];
    blocks: string[];
    technical_notes: string | null;
    implementation_details: any;
    api_changes: any;
    database_changes: any;
    test_scenarios: any;
    test_results: any;
    definition_of_done: any;
    actual_hours: number | null;
    estimated_hours: number | null;
    complexity_score: number | null;
    version: number;
    revision_history: any;
    tags: string[] | null;
    labels: any;
    custom_fields: any;
    attachments: any;
    deleted_at: string | null;
    archived_at: string | null;
}

interface UserStoriesState {
    userStoriesByFeature: Record<number, UserStory[]>;
    loading: boolean;
    pendingCount: number; // Track how many requests are in flight
    error: string | null;
}

const initialState: UserStoriesState = {
    userStoriesByFeature: {},
    loading: false,
    pendingCount: 0,
    error: null
};

// Async thunk to fetch user stories for a feature
export const fetchFeatureUserStories = createAsyncThunk(
    "userStories/fetchFeatureUserStories",
    async ({ featureId, idToken }: { featureId: number; idToken: string }, { rejectWithValue }) => {
        try {
            const response = await fetchFeatureUserStoriesApi(featureId, idToken);
            return { featureId, userStories: response };
        } catch (error: any) {
            return rejectWithValue(error.message || "Failed to fetch user stories");
        }
    }
);

// Async thunk to delete a user story
export const deleteUserStory = createAsyncThunk(
    "userStories/deleteUserStory",
    async ({ userStoryId, featureId, idToken }: { userStoryId: string; featureId: number; idToken: string }, { rejectWithValue }) => {
        try {
            await deleteUserStoryApi(userStoryId, idToken);
            return { userStoryId, featureId };
        } catch (error: any) {
            return rejectWithValue(error.message || "Failed to delete user story");
        }
    }
);

const userStoriesSlice = createSlice({
    name: "userStories",
    initialState,
    reducers: {
        clearUserStories: state => {
            state.userStoriesByFeature = {};
            state.error = null;
        },
        removeFeatureUserStories: (state, action: { payload: number }) => {
            // Remove user stories for a deleted feature
            // This triggers the hierarchy to show skeleton while recalculating
            delete state.userStoriesByFeature[action.payload];
        }
    },
    extraReducers: builder => {
        builder
            .addCase(fetchFeatureUserStories.pending, state => {
                state.pendingCount += 1;
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchFeatureUserStories.fulfilled, (state, action) => {
                state.pendingCount -= 1;
                state.loading = state.pendingCount > 0;
                state.userStoriesByFeature[action.payload.featureId] = action.payload.userStories;
            })
            .addCase(fetchFeatureUserStories.rejected, (state, action) => {
                state.pendingCount -= 1;
                state.loading = state.pendingCount > 0;
                state.error = action.payload as string;
            })
            .addCase(deleteUserStory.pending, state => {
                state.loading = true;
                state.error = null;
            })
            .addCase(deleteUserStory.fulfilled, (state, action) => {
                state.loading = false;
                const { userStoryId, featureId } = action.payload;
                if (state.userStoriesByFeature[featureId]) {
                    state.userStoriesByFeature[featureId] = state.userStoriesByFeature[featureId].filter(
                        story => story.id !== userStoryId
                    );
                }
            })
            .addCase(deleteUserStory.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            });
    }
});

export const { clearUserStories, removeFeatureUserStories } = userStoriesSlice.actions;
export default userStoriesSlice.reducer;
